import React, { useEffect, useState } from "react";
import {
  Typography,
  Space,
  Input,
  Button,
  Tabs,
  Collapse,
  message,
  Spin,
  Badge,
  Tag,
  Table,
  Card,
  Divider,
  Flex,
  Tooltip,
  Switch,
  Popconfirm,
  Select,
  theme,
  Alert
} from "antd";
import { useStore } from "../../stores/root-store-context";
import { observer } from "mobx-react-lite";
import Layout from "../../components/layout/Layout";
import { useTranslation } from "react-i18next";
import {
  CopyOutlined,
  SyncOutlined,
  DeleteOutlined,
  DownOutlined,
  RightOutlined,
  CodeOutlined,
  ApiOutlined,
  SearchOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined,
  GlobalOutlined,
  UserOutlined,
  ProjectOutlined,
  TagsOutlined,
  CommentOutlined,
  ClockCircleOutlined,
  FieldTimeOutlined,
  LockOutlined
} from "@ant-design/icons";
import SyntaxHighlighter from "react-syntax-highlighter";
import { docco, atomOneDark } from "react-syntax-highlighter/dist/esm/styles/hljs";
import { motion, AnimatePresence } from "framer-motion";
import { apiDocs } from "./ApiDocs";
import styles from "./IntegrationAPI.module.css";
import { CompanyApiKey } from "../../models/CompanyApiKey";

// Interfaces remain the same...
interface ApiParameter {
  name: string;
  type: string;
  required: boolean;
  description: string;
}

interface ApiRequestBody {
  type: string;
  properties: ApiParameter[];
}

interface ApiHeader {
  name: string;
  value: string;
  required: boolean;
  description: string;
}

interface ApiResponse {
  code: string;
  description: string;
  example: string;
}

interface ApiExample {
  request: string;
  response: string;
}

interface ApiEndpointDetails {
  summary: string;
  urlParams: ApiParameter[];
  queryParams: ApiParameter[];
  requestBody: ApiRequestBody | null;
  headers: ApiHeader[];
  responses: ApiResponse[];
  example: ApiExample;
}

interface ApiEndpoint {
  method: string;
  endpoint: string;
  description: string;
  details: ApiEndpointDetails;
}

interface ApiCategory {
  title: string;
  description: string;
  endpoints: ApiEndpoint[];
}

export interface ApiDocsType {
  [key: string]: ApiCategory;
}

const { TabPane } = Tabs;
const { Panel } = Collapse;
const { Title, Text, Paragraph } = Typography;
const { Search } = Input;

interface MethodBadgeProps {
  method: string;
}

const MethodBadge: React.FC<MethodBadgeProps> = ({ method }) => {
  const methodColors: any = {
    GET: "#61affe",
    POST: "#49cc90",
    PUT: "#fca130",
    DELETE: "#f93e3e",
    PATCH: "#50e3c2"
  };

  return (
    <Tag color={methodColors[method]} style={{ fontWeight: "bold", minWidth: "60px", textAlign: "center" }}>
      {method}
    </Tag>
  );
};

// Component for displaying a single endpoint
interface EndpointItemProps {
  endpoint: ApiEndpoint;
  expanded: boolean;
  onToggle: (endpoint: ApiEndpoint) => void;
  onCopy: (endpoint: ApiEndpoint) => void;
  unauthorized?: boolean;
}

const EndpointItem: React.FC<EndpointItemProps> = ({ endpoint, expanded, onToggle, onCopy, unauthorized }) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);
  const [showRequestExample, setShowRequestExample] = useState(true);

  const handleCopy = (text: any, type: any) => {
    navigator.clipboard.writeText(text).then(() => {
      setIsCopied(true);
      message.success(t(type === "endpoint" ? "EndpointCopied" : "ExampleCopied"));
      setTimeout(() => setIsCopied(false), 2000);
    }).catch(() => {
      message.error(t("CopyFailed"));
    });
  };

  return (
    <div className="endpoint-item" style={{ marginBottom: 16 }}>
      <Card
        hoverable
        style={{
          borderLeft: `4px solid ${endpoint.method === "GET" ? "#61affe" :
            endpoint.method === "POST" ? "#49cc90" :
              endpoint.method === "PUT" ? "#fca130" :
                endpoint.method === "DELETE" ? "#f93e3e" :
                  "#50e3c2"}`,
          transition: "all 0.3s"
        }}
        onClick={() => onToggle(endpoint)}
      >
        <Flex align="center" justify="space-between">
          <Flex align="center" gap={12}>
            <MethodBadge method={endpoint.method} />
            <Text strong style={{ fontSize: 16 }}>{endpoint.endpoint}</Text>
          </Flex>
          <Flex align="center" gap={8}>
            <Tooltip title={t("CopyEndpoint")}>
              <Button
                type="text"
                icon={isCopied ? <CheckCircleOutlined style={{ color: token.colorSuccess }} /> : <CopyOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy(endpoint.endpoint, "endpoint");
                }}
              />
            </Tooltip>
            <Button
              type="text"
              icon={expanded ? <DownOutlined /> : <RightOutlined />}
            />
          </Flex>
        </Flex>
        <Paragraph style={{ marginTop: 8, color: token.colorTextSecondary }}>
          {t(endpoint.description)}
        </Paragraph>
      </Card>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card style={{ marginTop: 1, backgroundColor: token.colorBgElevated }}>
              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                {/* Endpoint summary */}
                <div>
                  <Text strong>{t("Summary")}</Text>
                  <Paragraph>{endpoint.details.summary}</Paragraph>
                </div>

                {/* URL Parameters */}
                {endpoint.details.urlParams && endpoint.details.urlParams.length > 0 && (
                  <div>
                    <Text strong>{t("URLParameters")}</Text>
                    <Table
                      dataSource={endpoint.details.urlParams}
                      columns={[
                        { title: t("Name"), dataIndex: "name", key: "name" },
                        { title: t("Type"), dataIndex: "type", key: "type", render: (text) => <Tag>{text}</Tag> },
                        { title: t("Required"), dataIndex: "required", key: "required", render: (required) => required ? <CheckCircleOutlined style={{ color: token.colorSuccess }} /> : <CloseCircleOutlined style={{ color: token.colorError }} /> },
                        { title: t("Description"), dataIndex: "description", key: "description" }
                      ]}
                      size="small"
                      pagination={false}
                    />
                  </div>
                )}

                {/* Query Parameters */}
                {endpoint.details.queryParams && endpoint.details.queryParams.length > 0 && (
                  <div>
                    <Text strong>{t("QueryParameters")}</Text>
                    <Table
                      dataSource={endpoint.details.queryParams}
                      columns={[
                        { title: t("Name"), dataIndex: "name", key: "name" },
                        { title: t("Type"), dataIndex: "type", key: "type", render: (text) => <Tag>{text}</Tag> },
                        { title: t("Required"), dataIndex: "required", key: "required", render: (required) => required ? <CheckCircleOutlined style={{ color: token.colorSuccess }} /> : <CloseCircleOutlined style={{ color: token.colorError }} /> },
                        { title: t("Description"), dataIndex: "description", key: "description" }
                      ]}
                      size="small"
                      pagination={false}
                    />
                  </div>
                )}

                {/* Request Body */}
                {endpoint.details.requestBody && (
                  <div>
                    <Text strong>{t("RequestBody")}</Text>
                    <Table
                      dataSource={endpoint.details.requestBody.properties}
                      columns={[
                        { title: t("Name"), dataIndex: "name", key: "name" },
                        { title: t("Type"), dataIndex: "type", key: "type", render: (text) => <Tag>{text}</Tag> },
                        { title: t("Required"), dataIndex: "required", key: "required", render: (required) => required ? <CheckCircleOutlined style={{ color: token.colorSuccess }} /> : <CloseCircleOutlined style={{ color: token.colorError }} /> },
                        { title: t("Description"), dataIndex: "description", key: "description" }
                      ]}
                      size="small"
                      pagination={false}
                    />
                  </div>
                )}

                {/* Headers */}
                {endpoint.details.headers && endpoint.details.headers.length > 0 && (
                  <div>
                    <Text strong>{t("Headers")}</Text>
                    <Table
                      dataSource={endpoint.details.headers}
                      columns={[
                        { title: t("Name"), dataIndex: "name", key: "name" },
                        { title: t("Value"), dataIndex: "value", key: "value", render: (text) => <Text code>{text}</Text> },
                        { title: t("Required"), dataIndex: "required", key: "required", render: (required) => required ? <CheckCircleOutlined style={{ color: token.colorSuccess }} /> : <CloseCircleOutlined style={{ color: token.colorError }} /> },
                        { title: t("Description"), dataIndex: "description", key: "description" }
                      ]}
                      size="small"
                      pagination={false}
                    />
                  </div>
                )}

                {/* Responses */}
                {endpoint.details.responses && endpoint.details.responses.length > 0 && (
                  <div>
                    <Text strong>{t("Responses")}</Text>
                    <Table
                      dataSource={endpoint.details.responses}
                      columns={[
                        {
                          title: t("Code"),
                          dataIndex: "code",
                          key: "code",
                          render: (code) => {
                            const color = code.startsWith("2") ? "success" : code.startsWith("4") ? "error" : "warning";
                            return <Tag color={color}>{code}</Tag>;
                          }
                        },
                        { title: t("Description"), dataIndex: "description", key: "description" },
                        { title: t("Example"), dataIndex: "example", key: "example", render: (text) => <Text code ellipsis>{text}</Text> }
                      ]}
                      size="small"
                      pagination={false}
                    />
                  </div>
                )}

                {/* Example - For unauthorized users, show limited info */}
                {endpoint.details.example && !unauthorized && (
                  <div>
                    <Flex align="center" justify="space-between">
                      <Text strong>{t("Example")}</Text>
                      <Select
                        defaultValue="request"
                        style={{ width: 140 }}
                        onChange={(value) => setShowRequestExample(value === "request")}
                        options={[
                          { value: "request", label: t("Request") },
                          { value: "response", label: t("Response") }
                        ]}
                      />
                    </Flex>
                    <div style={{ position: "relative" }}>
                      <SyntaxHighlighter
                        language={showRequestExample ? "bash" : "json"}
                        style={atomOneDark}
                        customStyle={{ borderRadius: 8 }}
                      >
                        {showRequestExample ? endpoint.details.example.request : endpoint.details.example.response}
                      </SyntaxHighlighter>
                      <Tooltip title={t("CopyExample")}>
                        <Button
                          icon={<CopyOutlined />}
                          size="small"
                          type="text"
                          style={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            color: "white",
                            backgroundColor: "rgba(0,0,0,0.3)"
                          }}
                          onClick={() => handleCopy(
                            showRequestExample ? endpoint.details.example.request : endpoint.details.example.response,
                            "example"
                          )}
                        />
                      </Tooltip>
                    </div>
                  </div>
                )}
              </Space>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Add props interface for the main component
interface IntegrationAPIProps {
  unauthorized?: boolean;
}

// Main component
const IntegrationAPI: React.FC<IntegrationAPIProps> = observer(({ unauthorized = false }) => {
  const { applicationStore } = useStore();
  const [apiKey, setApiKey] = useState<CompanyApiKey | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeCategory, setActiveCategory] = useState<string>("apiIntegration");
  const [expandedEndpoint, setExpandedEndpoint] = useState<ApiEndpoint | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [copiedEndpoint, setCopiedEndpoint] = useState<ApiEndpoint | null>(null);
  const [darkMode, setDarkMode] = useState<boolean>(false);
  const { t } = useTranslation();
  const { token } = theme.useToken();

  useEffect(() => {
    // Only fetch API key if user is authorized
    if (!unauthorized) {
      setIsLoading(true);
      applicationStore.getAPI().then((res) => {
        if (res && res.length > 0) {
          setApiKey(res[0]);
        }
      }).finally(() => {
        setIsLoading(false);
      });
    }
  }, [unauthorized, applicationStore]);

  const handleCopyApiKey = () => {
    navigator.clipboard.writeText(apiKey?.key || "").then(() => {
      message.success(t("ApiKeySuccessfulCopied"));
    }).catch(() => {
      message.error(t("ApiKeyCopyFailed"));
    });
  };

  const handleGenerateNewKey = () => {
    setIsLoading(true);
    applicationStore.generateAPI().then((res) => {
      if (res && res.key) {
        setApiKey(res);
        message.success(t("ApiKeyGenerated"));
      }
    }).finally(() => {
      setIsLoading(false);
    });
  };

  const handleDeleteApiKey = () => {
    if (apiKey?.id) {
      setIsLoading(true);
      applicationStore.deleteAPI(apiKey?.id.toString()).then(() => {
        setApiKey(undefined);
        message.success(t("ApiKeyDeleted"));
      }).finally(() => {
        setIsLoading(false);
      });
    }
  };

  const handleEndpointToggle = (endpoint: ApiEndpoint) => {
    if (expandedEndpoint === endpoint) {
      setExpandedEndpoint(null);
    } else {
      setExpandedEndpoint(endpoint);
    }
  };

  const handleCopyEndpoint = (endpoint: ApiEndpoint) => {
    navigator.clipboard.writeText(endpoint.endpoint).then(() => {
      setCopiedEndpoint(endpoint);
      message.success(t("EndpointCopied"));
      setTimeout(() => setCopiedEndpoint(null), 2000);
    }).catch(() => {
      message.error(t("CopyFailed"));
    });
  };

  const filterEndpoints = (endpoints: ApiEndpoint[]) => {
    if (!searchQuery) return endpoints;

    return endpoints.filter(endpoint =>
      endpoint.endpoint.toLowerCase().includes(searchQuery.toLowerCase()) ||
      endpoint.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      endpoint.method.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // Calculate counts for each category
  const getCategoryCount = (category: string) => {
    return apiDocs[category].endpoints.length;
  };

  return (
    <Layout headerStyle={1} footerStyle={2}>
      <Spin spinning={isLoading}>
        <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
          {/* Header section with API key management - Only show full version for authorized users */}
          <Card style={{ marginBottom: 24 }}>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <Flex align="center" justify="space-between">
                <Title level={2} style={{ margin: 0 }}>
                  <ApiOutlined style={{ marginRight: 12 }} />
                  {t("IntegrationAPITitle")}
                </Title>

              </Flex>

              {/* Show different information for unauthorized users */}
              {unauthorized ? (
                <></>
              ) : (
                <>
                  <div>
                    <Paragraph>
                      <InfoCircleOutlined style={{ marginRight: 8 }} />
                      {t("ApiKeyDescription")}
                    </Paragraph>
                  </div>

                  <div>
                    <Text strong>{t("ApiKeyLabel")}</Text>
                    <Input.Group compact style={{ display: "flex", marginTop: 8 }}>
                      <Input
                        style={{ width: "calc(100% - 260px)" }}
                        value={apiKey?.key}
                        placeholder={!apiKey?.key ? t("NoApiKeyGenerated") : ""}
                        disabled={!apiKey?.key}
                        readOnly
                      />
                      <Flex>
                        <Tooltip title={t("CopyApiKey")}>
                          <Button
                            icon={<CopyOutlined />}
                            onClick={handleCopyApiKey}
                            disabled={!apiKey?.key}
                          >
                            {t("Copy")}
                          </Button>
                        </Tooltip>
                        <Popconfirm
                          title={
                            <div>
                              <div>{t("ConfirmGenerateNewKeyTitle")}</div>
                              <div>{t("ConfirmGenerateNewKeyDescription")}</div>
                            </div>
                          }
                          onConfirm={handleGenerateNewKey}
                          okText={t("Yes")}
                          cancelText={t("No")}
                          icon={<QuestionCircleOutlined style={{ color: token.colorWarning }} />}
                        >
                          <Button icon={<SyncOutlined />}>{t("GenerateNewKey")}</Button>
                        </Popconfirm>
                        <Popconfirm
                          title={
                            <div>
                              <div>{t("ConfirmDeleteKeyTitle")}</div>
                              <div>{t("ConfirmDeleteKeyDescription")}</div>
                            </div>
                          }
                          onConfirm={handleDeleteApiKey}
                          okText={t("Yes")}
                          cancelText={t("No")}
                          disabled={!apiKey?.key}
                          icon={<QuestionCircleOutlined style={{ color: token.colorError }} />}
                        >
                          <Button
                            icon={<DeleteOutlined />}
                            danger
                            disabled={!apiKey?.key}
                          >
                            {t("Delete")}
                          </Button>
                        </Popconfirm>
                      </Flex>
                    </Input.Group>
                  </div>
                </>
              )}
            </Space>
          </Card>

          {/* API documentation section */}
          <Card>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <Title level={3} style={{ margin: 0 }}>
                <CodeOutlined style={{ marginRight: 12 }} />
                {t("AvailableEndpoints")}
              </Title>

              {/* Search bar for endpoints */}
              <Search
                placeholder={t("SearchEndpoints")}
                allowClear
                enterButton={<SearchOutlined />}
                size="large"
                onSearch={setSearchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ marginBottom: 16 }}
              />

              {/* API categories */}
              <Tabs
                className={styles.tabsAutoWidth}
                activeKey={activeCategory}
                onChange={setActiveCategory}
                type="card"
                size="large"
                tabBarStyle={{ marginBottom: 24 }}
                items={[
                  {
                    key: "apiIntegration",
                    label: (
                      <Flex align="center" gap={8}>
                        <UserOutlined />
                        <span>{t(apiDocs.apiIntegration.title)}</span>
                        <Badge count={getCategoryCount("apiIntegration")} style={{ backgroundColor: token.colorPrimary }} />
                      </Flex>
                    ),
                    children: (
                      <div className="endpoints-list">
                        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                          {t(apiDocs.apiIntegration.description)}
                        </Paragraph>

                        {filterEndpoints(apiDocs.apiIntegration.endpoints).map((endpoint, index) => (
                          <EndpointItem
                            key={index}
                            endpoint={endpoint}
                            expanded={expandedEndpoint === endpoint}
                            onToggle={handleEndpointToggle}
                            onCopy={handleCopyEndpoint}
                            unauthorized={unauthorized}
                          />
                        ))}
                      </div>
                    )
                  },
                  {
                    key: "kanbanTask",
                    label: (
                      <Flex align="center" gap={8}>
                        <ProjectOutlined />
                        <span>{t(apiDocs.kanbanTask.title)}</span>
                        <Badge count={getCategoryCount("kanbanTask")} style={{ backgroundColor: token.colorPrimary }} />
                      </Flex>
                    ),
                    children: (
                      <div className="endpoints-list">
                        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                          {t(apiDocs.kanbanTask.description)}
                        </Paragraph>

                        {filterEndpoints(apiDocs.kanbanTask.endpoints).map((endpoint, index) => (
                          <EndpointItem
                            key={index}
                            endpoint={endpoint}
                            expanded={expandedEndpoint === endpoint}
                            onToggle={handleEndpointToggle}
                            onCopy={handleCopyEndpoint}
                            unauthorized={unauthorized}
                          />
                        ))}
                      </div>
                    )
                  },
                  {
                    key: "kanbanTaskStatus",
                    label: (
                      <Flex align="center" gap={8}>
                        <TagsOutlined />
                        <span>{t(apiDocs.kanbanTaskStatus.title)}</span>
                        <Badge count={getCategoryCount("kanbanTaskStatus")} style={{ backgroundColor: token.colorPrimary }} />
                      </Flex>
                    ),
                    children: (
                      <div className="endpoints-list">
                        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                          {t(apiDocs.kanbanTaskStatus.description)}
                        </Paragraph>

                        {filterEndpoints(apiDocs.kanbanTaskStatus.endpoints).map((endpoint, index) => (
                          <EndpointItem
                            key={index}
                            endpoint={endpoint}
                            expanded={expandedEndpoint === endpoint}
                            onToggle={handleEndpointToggle}
                            onCopy={handleCopyEndpoint}
                            unauthorized={unauthorized}
                          />
                        ))}
                      </div>
                    )
                  }
                ]}
              />
            </Space>
          </Card>
        </div>
      </Spin>
    </Layout>
  );
});

export default IntegrationAPI;